/* =========================================================================
   Tech-Life 2026 - multilink.js (Premium build)
   Interactive coverage visualizer. Loads only on /pages/multilink.

   Features:
     - Click / tap to drop a pin on the active scene
     - Drag to reposition (pointer events)
     - Tap a pin to select it; arrow keys nudge a selected pin
     - Per-pin remove via X button
     - Speaker model dock: pick which model new pins use (per-model
       coverage radius, accent, thumbnail, product). Falls back to the
       section-level single/bundle products when no models configured.
     - Animated connection lines between pins in drop order (SVG layer)
     - Coverage circle visualization (per-model radius, or % of scene width)
     - Grid overlay toggle
     - Coverage overlay toggle
     - Real-world sq ft from per-scene width (set in customizer)
     - Smart-blend bundle math
         per model: 2+ pins with a bundle product -> 1 bundle + (N-2) singles
         legacy (no models): 1 pin -> single, 2 -> bundle, 3+ -> bundle + singles
     - Add-to-cart adds the full breakdown in one batch
     - Undo last action (last 25 ops)
     - Clear all pins on the active scene
     - Share URL: encodes scene + pins (x,y,m); legacy x,y URLs still load
     - localStorage auto-save per scene (v2; migrates v1 snapshots)
     - First-visit coachmark on the canvas
     - ARIA-live announcements for screen readers
     - prefers-reduced-motion + theme animation toggle respected
   ========================================================================= */
(function () {
  'use strict';

  var root = document.querySelector('[data-multilink]');
  if (!root) return;

  var $  = function (sel, ctx) { return (ctx || root).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); };

  // ---------- Config from data attrs ----------
  var MAX_PINS    = parseInt(root.getAttribute('data-max-pins') || '8', 10);
  var COVER_PCT   = parseFloat(root.getAttribute('data-coverage-pct') || '18');
  var SINGLE      = root.getAttribute('data-single-product') || '';
  var BUNDLE      = root.getAttribute('data-bundle-product') || '';
  var GRID_ON     = root.getAttribute('data-grid-default') === '1';
  var COVER_ON    = root.getAttribute('data-coverage-default') !== '0';
  var STORAGE_KEY = root.getAttribute('data-storage-key') || 'tl-multilink-v2';
  var LEGACY_KEY  = root.getAttribute('data-legacy-storage-key') || '';
  var COACH_KEY   = root.getAttribute('data-coachmark-key') || 'tl-multilink-coach-v1';
  var SHARE_LABEL = root.getAttribute('data-share-label') || 'Link copied';

  // Expose coverage % to CSS as a unitless number (used with cqw)
  root.style.setProperty('--cov-pct', String(COVER_PCT));

  // Animation gate: ticker only runs when the theme toggle is on and the
  // user has not asked for reduced motion. CSS handles its own guards.
  var REDUCE  = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var ANIM_OK = document.body.classList.contains('anim-on') && !REDUCE;

  // ---------- DOM cache ----------
  var scenes      = $$('[data-ml-scene-id]');
  if (!scenes.length) return;
  var tabs        = $$('[data-ml-scene]');
  var liveEl      = $('[data-ml-live]');
  var countEl     = $('[data-ml-count]');
  var coverEl     = $('[data-ml-coverage]');
  var linkedEl    = $('[data-ml-linked]');
  var linkedText  = $('[data-ml-linked-text]');
  var recWrap     = $('[data-ml-rec]');
  var recTitle    = $('[data-ml-rec-title]');
  var recBlurb    = $('[data-ml-rec-blurb]');
  var recBtn      = $('[data-ml-rec-add]');
  var recLines    = $('[data-ml-rec-lines]');
  var recTotal    = $('[data-ml-rec-total]');
  var recFine     = $('[data-ml-rec-fine]');
  var undoBtn     = $('[data-ml-undo]');
  var shareBtn    = $('[data-ml-share]');
  var shareLabel  = $('[data-ml-share-label]');
  var clearBtn    = $('[data-ml-clear]');
  var toggles     = $$('[data-ml-toggle]');
  var coachmark   = $('[data-ml-coachmark]');
  var coachClose  = $('[data-ml-coach-dismiss]');
  var helpBtn     = $('[data-ml-help]');
  var stepsEl     = $('[data-ml-steps]');
  var modelBtns   = $$('[data-ml-model]');

  // ---------- Helpers ----------
  var clamp = function (n, lo, hi) { return Math.max(lo, Math.min(hi, n)); };
  var esc = function (s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };
  var announce = function (msg) {
    if (liveEl) {
      liveEl.textContent = '';
      // small async kick so SRs re-read
      setTimeout(function () { liveEl.textContent = msg; }, 30);
    }
  };

  // ---------- Speaker models (from dock buttons) ----------
  var ACCENT_VARS = { red: 'var(--tl-red)', navy: 'var(--tl-navy)', link: 'var(--tl-link)' };
  var MODELS = modelBtns.map(function (btn) {
    return {
      title: btn.getAttribute('data-model-title') || 'Speaker',
      label: btn.getAttribute('data-model-label') || '',
      radiusFt: parseFloat(btn.getAttribute('data-model-radius') || '10'),
      accentVar: ACCENT_VARS[btn.getAttribute('data-model-accent')] || ACCENT_VARS.red,
      thumb: btn.getAttribute('data-model-thumb') || '',
      handle: btn.getAttribute('data-model-handle') || '',
      variantId: parseInt(btn.getAttribute('data-model-variant') || '', 10) || 0,
      price: parseInt(btn.getAttribute('data-model-price') || '', 10) || 0,
      bundleVariantId: parseInt(btn.getAttribute('data-model-bundle-variant') || '', 10) || 0,
      bundlePrice: parseInt(btn.getAttribute('data-model-bundle-price') || '', 10) || 0
    };
  });
  var activeModel = 0;
  var clampModel = function (m) {
    return MODELS.length ? clamp(parseInt(m, 10) || 0, 0, MODELS.length - 1) : 0;
  };
  var pinModel = function (p) { return MODELS.length ? MODELS[clampModel(p.m)] : null; };

  function setActiveModel(i, opts) {
    if (!MODELS.length) return;
    opts = opts || {};
    i = clamp(i, 0, MODELS.length - 1);
    activeModel = i;
    modelBtns.forEach(function (btn, j) {
      var on = j === i;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.tabIndex = on ? 0 : -1;
      if (on && opts.focus) btn.focus();
    });
    if (!opts.silent) announce(MODELS[i].title + ' selected. New pins use ' + MODELS[i].title + '.');
  }
  modelBtns.forEach(function (btn, i) {
    btn.addEventListener('click', function () { setActiveModel(i); });
    btn.addEventListener('keydown', function (e) {
      var dir = 0;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   dir = -1;
      if (dir) {
        e.preventDefault();
        setActiveModel((activeModel + dir + MODELS.length) % MODELS.length, { focus: true });
      }
    });
  });
  if (MODELS.length) setActiveModel(0, { silent: true });

  // ---------- Coverage math ----------
  // Per-pin coverage diameter as a % of the scene width. Models carry a
  // real-world radius in ft; without models, fall back to the section pct.
  var pinCoveragePct = function (p, widthFt) {
    var m = pinModel(p);
    if (!m) return COVER_PCT;
    return clamp((2 * m.radiusFt / widthFt) * 100, 4, 80);
  };
  var pinRadiusFt = function (p, widthFt) {
    var m = pinModel(p);
    return m ? m.radiusFt : widthFt * (COVER_PCT / 100) / 2;
  };
  var coverageAreaSqFt = function (pins, widthFt) {
    if (!pins.length) return 0;
    // approximate union area: first circle full, each additional adds 60% (overlap discount)
    var total = 0;
    pins.forEach(function (p, i) {
      var r = pinRadiusFt(p, widthFt);
      total += Math.PI * r * r * (i === 0 ? 1 : 0.6);
    });
    return Math.round(total);
  };

  // ---------- State ----------
  // sceneState[sceneId] = { pins: [{x,y,m}], widthFt: 30, title: 'Tailgate', selected: -1 }
  var sceneState = {};
  scenes.forEach(function (s) {
    var id = s.getAttribute('data-ml-scene-id');
    sceneState[id] = {
      pins: [],
      widthFt: parseFloat(s.getAttribute('data-ml-scene-width') || '30'),
      title: s.getAttribute('data-ml-scene-title') || 'Scene',
      selected: -1
    };
  });
  var activeId = scenes[0].getAttribute('data-ml-scene-id');
  var history = []; // [{ sceneId, kind: 'add'|'remove'|'move'|'clear', payload }]
  var HISTORY_LIMIT = 25;

  var normalizePin = function (p) {
    return {
      x: clamp(parseFloat(p.x) || 0, 0, 100),
      y: clamp(parseFloat(p.y) || 0, 0, 100),
      m: clampModel(p.m)
    };
  };

  var sceneEl = function (id) {
    for (var i = 0; i < scenes.length; i++) {
      if (scenes[i].getAttribute('data-ml-scene-id') === id) return scenes[i];
    }
    return null;
  };

  // ---------- Persistence ----------
  var saveState = function () {
    try {
      var snap = {};
      Object.keys(sceneState).forEach(function (id) {
        snap[id] = { pins: sceneState[id].pins };
      });
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ activeId: activeId, scenes: snap }));
    } catch (e) { /* quota / private mode -> silent */ }
  };

  var applySnapshot = function (snap) {
    if (!snap || !snap.scenes) return;
    Object.keys(snap.scenes).forEach(function (id) {
      if (sceneState[id]) {
        sceneState[id].pins = (snap.scenes[id].pins || []).slice(0, MAX_PINS).map(normalizePin);
      }
    });
    if (snap.activeId && sceneState[snap.activeId]) {
      activeId = snap.activeId;
      activateTab(activeId, true);
    }
  };

  var loadState = function () {
    // 1. URL params win over localStorage
    var loadedFromURL = loadFromURL();
    if (loadedFromURL) return;

    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw && LEGACY_KEY) raw = window.localStorage.getItem(LEGACY_KEY); // v1 -> v2 migration (pins get m:0)
      if (!raw) return;
      applySnapshot(JSON.parse(raw));
    } catch (e) { /* silent */ }
  };

  // ---------- Share URL: ?scene=<id>&pins=x,y,m;x,y,m;... ----------
  // Legacy x,y pairs (no model token) still parse; old builds ignore the
  // extra token, so v2 links degrade gracefully too.
  var encodeShare = function () {
    var st = sceneState[activeId];
    var p = st.pins.map(function (pin) {
      var bits = Math.round(pin.x) + ',' + Math.round(pin.y);
      if (MODELS.length) bits += ',' + clampModel(pin.m);
      return bits;
    }).join(';');
    var u = new URL(window.location.href);
    u.searchParams.set('scene', activeId);
    u.searchParams.set('pins', p);
    return u.toString();
  };

  var loadFromURL = function () {
    try {
      var u = new URL(window.location.href);
      var sceneParam = u.searchParams.get('scene');
      var pinsParam  = u.searchParams.get('pins');
      if (!sceneParam || !pinsParam || !sceneState[sceneParam]) return false;
      var pins = pinsParam.split(';').filter(Boolean).map(function (pair) {
        var t = pair.split(',');
        return normalizePin({ x: t[0], y: t[1], m: t.length > 2 ? t[2] : 0 });
      }).slice(0, MAX_PINS);
      sceneState[sceneParam].pins = pins;
      activeId = sceneParam;
      activateTab(sceneParam, true);
      return true;
    } catch (e) { return false; }
  };

  // ---------- Tab activation ----------
  function activateTab(id, silent) {
    tabs.forEach(function (t) {
      var on = t.getAttribute('data-ml-scene') === id;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    scenes.forEach(function (s) {
      s.classList.toggle('is-active', s.getAttribute('data-ml-scene-id') === id);
    });
    activeId = id;
    sceneState[id].selected = -1;
    renderPins(id);
    updateUI();
    if (!silent) announce('Switched to ' + sceneState[id].title);
  }

  tabs.forEach(function (tab) {
    tab.addEventListener('click', function () {
      activateTab(tab.getAttribute('data-ml-scene'));
    });
  });

  // ---------- Click to drop pin ----------
  scenes.forEach(function (scene) {
    var canvas = $('[data-ml-canvas]', scene);
    if (!canvas) return;

    canvas.addEventListener('click', function (e) {
      if (e.target.closest('[data-ml-pin]')) return;
      if (e.target.closest('[data-ml-coachmark]')) return;
      var id = scene.getAttribute('data-ml-scene-id');
      var st = sceneState[id];
      if (st.pins.length >= MAX_PINS) {
        announce('Maximum of ' + MAX_PINS + ' pins reached on this scene.');
        return;
      }
      var rect = canvas.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width)  * 100;
      var y = ((e.clientY - rect.top)  / rect.height) * 100;
      st.pins.push({ x: x, y: y, m: activeModel });
      pushHistory({ sceneId: id, kind: 'add', payload: { idx: st.pins.length - 1 } });
      st.selected = st.pins.length - 1;
      renderPins(id);
      updateUI();
      var modelBit = MODELS.length ? (' (' + MODELS[activeModel].title + ')') : '';
      announce('Pin ' + st.pins.length + modelBit + ' placed on ' + st.title + '.');
      dismissCoachmark();
    });

    // Keyboard: arrow keys nudge selected pin (1% / 5% with shift)
    canvas.addEventListener('keydown', function (e) {
      var id = scene.getAttribute('data-ml-scene-id');
      var st = sceneState[id];
      if (st.selected < 0 || st.selected >= st.pins.length) return;
      var step = e.shiftKey ? 5 : 1;
      var moved = false;
      var p = st.pins[st.selected];
      if (e.key === 'ArrowLeft')  { p.x = clamp(p.x - step, 0, 100); moved = true; }
      if (e.key === 'ArrowRight') { p.x = clamp(p.x + step, 0, 100); moved = true; }
      if (e.key === 'ArrowUp')    { p.y = clamp(p.y - step, 0, 100); moved = true; }
      if (e.key === 'ArrowDown')  { p.y = clamp(p.y + step, 0, 100); moved = true; }
      if (moved) {
        e.preventDefault();
        renderPins(id);
        saveState();
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        removePin(id, st.selected);
      }
    });
  });

  // ---------- Render connection lines ----------
  // Chain in drop order: pin 1 -> 2 -> ... -> N. SVG viewBox is 0 0 100 100
  // with preserveAspectRatio="none", so pin % coords map 1:1 to user units.
  // vector-effect="non-scaling-stroke" keeps stroke + dashes undistorted.
  function renderLinks(id) {
    var scene = sceneEl(id);
    if (!scene) return;
    var svg = $('[data-ml-links]', scene);
    if (!svg) return;
    var st = sceneState[id];
    var html = '';
    for (var i = 1; i < st.pins.length; i++) {
      var a = st.pins[i - 1];
      var b = st.pins[i];
      html += '<line class="ml-link" x1="' + a.x + '" y1="' + a.y + '" x2="' + b.x + '" y2="' + b.y + '"' +
              ' vector-effect="non-scaling-stroke" style="animation-delay:-' + (i * 0.4) + 's"></line>';
    }
    svg.innerHTML = html;
  }

  // Cheap per-frame update while dragging: only the <=2 segments touching
  // the dragged pin change. Line k connects pins[k] and pins[k+1].
  function updateLinks(id, idx) {
    var scene = sceneEl(id);
    if (!scene) return;
    var svg = $('[data-ml-links]', scene);
    if (!svg) return;
    var p = sceneState[id].pins[idx];
    if (!p) return;
    var lines = svg.children;
    if (idx - 1 >= 0 && lines[idx - 1]) {
      lines[idx - 1].setAttribute('x2', p.x);
      lines[idx - 1].setAttribute('y2', p.y);
    }
    if (lines[idx]) {
      lines[idx].setAttribute('x1', p.x);
      lines[idx].setAttribute('y1', p.y);
    }
  }

  // ---------- Render pins ----------
  function renderPins(id) {
    var scene = sceneEl(id);
    if (!scene) return;
    var st = sceneState[id];
    var wrap = $('[data-ml-pins]', scene);
    if (!wrap) return;
    var html = st.pins.map(function (p, i) {
      var sel = (i === st.selected) ? ' is-selected' : '';
      var m = pinModel(p);
      var accent = m ? m.accentVar : 'var(--tl-red)';
      var covPct = pinCoveragePct(p, st.widthFt);
      var inner;
      if (m && m.thumb) {
        inner = '<img src="' + esc(m.thumb) + '" alt="" width="28" height="28" loading="lazy">';
      } else if (m && m.label) {
        inner = esc(m.label);
      } else {
        inner = String(i + 1);
      }
      var aria = 'Pin ' + (i + 1) + (m ? ', ' + esc(m.title) : '') + '. Drag to move, press Delete to remove.';
      return '' +
        '<div class="ml-pin' + sel + '" data-ml-pin data-idx="' + i + '" ' +
             'style="left:' + p.x + '%;top:' + p.y + '%;--cov:' + covPct.toFixed(2) + ';--pin-accent:' + accent + ';--i:' + i + '" ' +
             'tabindex="0" role="button" aria-label="' + aria + '">' +
          '<span class="ml-pin__coverage" aria-hidden="true"></span>' +
          '<span class="ml-pin__halo" aria-hidden="true"></span>' +
          '<span class="ml-pin__dot">' + inner + '</span>' +
          '<button class="ml-pin__remove" type="button" aria-label="Remove pin ' + (i + 1) + '" data-ml-pin-remove>&times;</button>' +
        '</div>';
    }).join('');
    wrap.innerHTML = html;
    renderLinks(id);
    wirePinInteractions(id);
  }

  // ---------- Pin interactions: drag, select, remove ----------
  function wirePinInteractions(id) {
    var scene = sceneEl(id);
    if (!scene) return;
    var canvas = $('[data-ml-canvas]', scene);
    var st = sceneState[id];
    $$('[data-ml-pin]', scene).forEach(function (pinEl) {
      var idx = parseInt(pinEl.getAttribute('data-idx'), 10);

      var removeBtn = $('[data-ml-pin-remove]', pinEl);
      if (removeBtn) {
        removeBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          removePin(id, idx);
        });
      }

      pinEl.addEventListener('focus', function () {
        st.selected = idx;
        $$('[data-ml-pin]', scene).forEach(function (el) { el.classList.toggle('is-selected', el === pinEl); });
      });

      var dragging = false;
      var dragStart = null;
      pinEl.addEventListener('pointerdown', function (e) {
        if (e.target.closest('[data-ml-pin-remove]')) return;
        dragging = true;
        dragStart = { x: st.pins[idx].x, y: st.pins[idx].y };
        st.selected = idx;
        if (pinEl.setPointerCapture) pinEl.setPointerCapture(e.pointerId);
        pinEl.classList.add('is-dragging', 'is-selected');
      });
      pinEl.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var rect = canvas.getBoundingClientRect();
        var x = clamp(((e.clientX - rect.left) / rect.width)  * 100, 0, 100);
        var y = clamp(((e.clientY - rect.top)  / rect.height) * 100, 0, 100);
        st.pins[idx].x = x;
        st.pins[idx].y = y;
        pinEl.style.left = x + '%';
        pinEl.style.top  = y + '%';
        updateLinks(id, idx);
      });
      var endDrag = function () {
        if (!dragging) return;
        dragging = false;
        pinEl.classList.remove('is-dragging');
        var moved = (Math.abs(st.pins[idx].x - dragStart.x) > 0.5 || Math.abs(st.pins[idx].y - dragStart.y) > 0.5);
        if (moved) {
          pushHistory({ sceneId: id, kind: 'move', payload: { idx: idx, from: dragStart, to: { x: st.pins[idx].x, y: st.pins[idx].y } } });
        }
        saveState();
      };
      pinEl.addEventListener('pointerup', endDrag);
      pinEl.addEventListener('pointercancel', endDrag);
    });
  }

  function removePin(id, idx) {
    var st = sceneState[id];
    if (idx < 0 || idx >= st.pins.length) return;
    var removed = st.pins.splice(idx, 1)[0];
    pushHistory({ sceneId: id, kind: 'remove', payload: { idx: idx, pin: removed } });
    if (st.selected === idx) st.selected = -1;
    else if (st.selected > idx) st.selected -= 1;
    renderPins(id);
    updateUI();
    announce('Pin ' + (idx + 1) + ' removed.');
  }

  // ---------- Undo ----------
  function pushHistory(op) {
    history.push(op);
    if (history.length > HISTORY_LIMIT) history.shift();
    refreshUndoBtn();
  }
  function refreshUndoBtn() {
    if (undoBtn) undoBtn.disabled = history.length === 0;
  }
  function undo() {
    var op = history.pop();
    if (!op) return;
    var st = sceneState[op.sceneId];
    if (!st) return;
    if (op.sceneId !== activeId) activateTab(op.sceneId, true);

    if (op.kind === 'add') {
      st.pins.splice(op.payload.idx, 1);
    } else if (op.kind === 'remove') {
      st.pins.splice(op.payload.idx, 0, op.payload.pin);
    } else if (op.kind === 'move') {
      var pin = st.pins[op.payload.idx];
      if (pin) { pin.x = op.payload.from.x; pin.y = op.payload.from.y; }
    } else if (op.kind === 'clear') {
      st.pins = op.payload.pins.slice();
    }
    renderPins(op.sceneId);
    updateUI();
    announce('Undone.');
    refreshUndoBtn();
  }
  if (undoBtn) undoBtn.addEventListener('click', undo);

  // ---------- Clear ----------
  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      var st = sceneState[activeId];
      if (!st.pins.length) return;
      pushHistory({ sceneId: activeId, kind: 'clear', payload: { pins: st.pins.slice() } });
      st.pins = [];
      st.selected = -1;
      renderPins(activeId);
      updateUI();
      announce('Pins cleared.');
    });
  }

  // ---------- Toggles (coverage / grid) ----------
  function setToggle(name, on) {
    if (name === 'coverage') {
      root.classList.toggle('show-coverage', on);
    } else if (name === 'grid') {
      root.classList.toggle('show-grid', on);
    }
    var btn = root.querySelector('[data-ml-toggle="' + name + '"]');
    if (btn) {
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }
  toggles.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var name = btn.getAttribute('data-ml-toggle');
      var on = btn.getAttribute('aria-pressed') !== 'true';
      setToggle(name, on);
    });
  });
  setToggle('coverage', COVER_ON);
  setToggle('grid', GRID_ON);

  // ---------- Share ----------
  if (shareBtn) {
    shareBtn.addEventListener('click', function () {
      var url = encodeShare();
      var done = function () {
        if (shareLabel) shareLabel.textContent = SHARE_LABEL;
        shareBtn.classList.add('is-copied');
        announce(SHARE_LABEL + '.');
        setTimeout(function () {
          if (shareLabel) shareLabel.textContent = 'Share';
          shareBtn.classList.remove('is-copied');
        }, 2200);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(function () {
          fallbackCopy(url); done();
        });
      } else {
        fallbackCopy(url); done();
      }
    });
  }
  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }

  // ---------- Coachmark ----------
  // The hint pill and the coachmark share the canvas center; only one may
  // show at a time. syncHint() owns that decision.
  function syncHint() {
    var scene = sceneEl(activeId);
    if (!scene) return;
    var sceneHint = $('[data-ml-hint]', scene);
    if (!sceneHint) return;
    var coachOpen = coachmark && !coachmark.hidden;
    sceneHint.style.display = (sceneState[activeId].pins.length > 0 || coachOpen) ? 'none' : '';
  }
  function showCoachmark(focusDismiss) {
    if (!coachmark) return;
    coachmark.hidden = false;
    syncHint();
    if (focusDismiss && coachClose) coachClose.focus();
  }
  function dismissCoachmark() {
    if (!coachmark || coachmark.hidden) return;
    coachmark.hidden = true;
    try { window.localStorage.setItem(COACH_KEY, '1'); } catch (e) {}
    syncHint();
  }
  if (coachClose) coachClose.addEventListener('click', dismissCoachmark);
  if (helpBtn) {
    helpBtn.addEventListener('click', function () {
      showCoachmark(true);
      announce('How it works guide opened.');
    });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && coachmark && !coachmark.hidden) dismissCoachmark();
  });
  function maybeShowCoachmark() {
    if (!coachmark) return;
    var seen = false;
    try { seen = window.localStorage.getItem(COACH_KEY) === '1'; } catch (e) {}
    var anyPins = Object.keys(sceneState).some(function (id) { return sceneState[id].pins.length > 0; });
    if (!seen && !anyPins) showCoachmark(false);
  }

  // ---------- Smart-blend bundle math ----------
  // Legacy path (no speaker model blocks): section-level handles, resolved
  // to variants at add time. Unchanged from the original build.
  function buildLineItems(n) {
    if (n <= 0) return [];
    if (n === 1)  return [{ handle: SINGLE, qty: 1, label: 'Single speaker' }];
    if (n === 2)  return [{ handle: BUNDLE || SINGLE, qty: 1, label: BUNDLE ? '2-pack bundle' : 'Single speaker', singlesFallback: !BUNDLE ? 2 : null }];
    // 3+
    var lines = [];
    if (BUNDLE) {
      lines.push({ handle: BUNDLE, qty: 1, label: '2-pack bundle' });
      lines.push({ handle: SINGLE, qty: n - 2, label: 'Add-on speaker' });
    } else {
      lines.push({ handle: SINGLE, qty: n, label: 'Single speaker' });
    }
    return lines.filter(function (l) { return l.qty > 0 && l.handle; });
  }

  // Model path: group pins by model; per model, prefer 1 bundle + singles
  // when a 2-pack bundle product is configured. Variant ids come straight
  // from the section markup, so add-to-cart is one batched call.
  function buildModelLines(pins) {
    var counts = {};
    pins.forEach(function (p) {
      var m = clampModel(p.m);
      counts[m] = (counts[m] || 0) + 1;
    });
    var lines = [];
    Object.keys(counts).sort(function (a, b) { return a - b; }).forEach(function (key) {
      var m = MODELS[parseInt(key, 10)];
      var count = counts[key];
      if (m.bundleVariantId && count >= 2) {
        lines.push({ variantId: m.bundleVariantId, qty: 1, label: m.title + ' 2-pack bundle', price: m.bundlePrice });
        if (count > 2) lines.push({ variantId: m.variantId, qty: count - 2, label: m.title, price: m.price });
      } else {
        lines.push({ variantId: m.variantId, qty: count, label: m.title, price: m.price });
      }
    });
    return lines;
  }

  function recommendationCopy(n, sqFt) {
    if (n === 1) return { eyebrow: 'Single setup', title: '1 speaker', blurb: 'Big sound for one zone. Add a second any time and the pair stays in sync.' };
    if (n === 2) return { eyebrow: 'Stereo pair', title: '2 speakers', blurb: 'Classic MultiLink pair. Stays in sync, no lag, no drift. Roughly ' + sqFt.toLocaleString() + ' sq ft of coverage.' };
    if (n <= 4) return { eyebrow: 'Patio-grade', title: n + ' speakers', blurb: 'Built for a backyard or patio. ~' + sqFt.toLocaleString() + ' sq ft, evenly covered.' };
    if (n <= 7) return { eyebrow: 'Yard / lot', title: n + ' speakers', blurb: 'A real outdoor zone. ~' + sqFt.toLocaleString() + ' sq ft. Volume that holds up over distance.' };
    return     { eyebrow: 'Venue-grade', title: n + ' speakers', blurb: 'Field, jobsite, or full venue. ~' + sqFt.toLocaleString() + ' sq ft. Ask us about a custom stack.' };
  }

  // ---------- Animated count ticker ----------
  function tickTo(el, target) {
    if (!el) return;
    var from = parseInt(el.textContent, 10);
    if (isNaN(from)) from = 0;
    if (!ANIM_OK || from === target) {
      el.textContent = String(target);
      return;
    }
    var start = null;
    var DUR = 350;
    var step = function (ts) {
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / DUR);
      var eased = 1 - Math.pow(1 - t, 3);
      el.textContent = String(Math.round(from + (target - from) * eased));
      if (t < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }

  var money = function (cents) {
    if (window.TL && window.TL.Money && window.TL.Money.format) return window.TL.Money.format(cents);
    return '';
  };

  // ---------- Update UI ----------
  function updateUI() {
    var st = sceneState[activeId];
    var n = st.pins.length;
    if (countEl) tickTo(countEl, n);

    if (linkedEl) {
      if (n >= 2) {
        linkedEl.hidden = false;
        if (linkedText) linkedText.textContent = n + ' speakers linked';
      } else {
        linkedEl.hidden = true;
      }
    }

    var sqFt = coverageAreaSqFt(st.pins, st.widthFt);
    if (coverEl) {
      coverEl.textContent = n
        ? ('~' + sqFt.toLocaleString() + ' sq ft of sound across this ' + st.widthFt + ' ft-wide scene')
        : 'Drop a pin to see your coverage';
    }

    syncHint();
    if (stepsEl) stepsEl.hidden = n > 0;

    if (n === 0) {
      if (recWrap) recWrap.hidden = true;
      saveState();
      refreshUndoBtn();
      return;
    }
    if (recWrap) recWrap.hidden = false;

    // copy
    var copy = recommendationCopy(n, sqFt);
    if (recTitle) recTitle.textContent = copy.title;
    if (recBlurb) recBlurb.textContent = copy.blurb;
    var eyebrowEl = $('[data-ml-rec-eyebrow]');
    if (eyebrowEl) eyebrowEl.textContent = copy.eyebrow;

    if (MODELS.length) {
      updateRecModels(st, n);
    } else {
      updateRecLegacy(n);
    }

    saveState();
    refreshUndoBtn();
  }

  // Model-aware recommendation: grouped per-model line items with prices
  // and a single batched add-to-cart using known variant ids.
  function updateRecModels(st, n) {
    var lines = buildModelLines(st.pins);
    var purchasable = lines.every(function (l) { return !!l.variantId; });
    var totalCents = lines.reduce(function (s, l) { return s + (l.price || 0) * l.qty; }, 0);
    var priced = purchasable && lines.every(function (l) { return l.price > 0; });

    if (recLines) {
      recLines.hidden = false;
      recLines.innerHTML = lines.map(function (l) {
        var priceBit = (l.price > 0 && money(l.price * l.qty))
          ? ' <span class="ml-vis__rec-price spec-value">' + money(l.price * l.qty) + '</span>'
          : '';
        return '<li><span class="ml-vis__rec-qty">' + l.qty + ' x</span> <span class="ml-vis__rec-name">' + esc(l.label) + '</span>' + priceBit + '</li>';
      }).join('');
    }
    if (recTotal) {
      if (priced && lines.length > 1 && money(totalCents)) {
        recTotal.hidden = false;
        recTotal.textContent = 'Total ' + money(totalCents);
      } else {
        recTotal.hidden = true;
        recTotal.textContent = '';
      }
    }
    if (recBtn) {
      recBtn.disabled = !purchasable;
      recBtn.textContent = n > 1 ? ('Add ' + n + ' to cart') : 'Add to cart';
      recBtn.onclick = function () { addModelLines(lines); };
    }
    if (recFine) {
      recFine.textContent = !purchasable
        ? 'Connect a product to each speaker model in the customizer to enable add-to-cart.'
        : (n >= 8 ? 'Setups this big often need a custom recommendation. We will follow up after checkout.' : '');
    }
  }

  // Legacy recommendation: unchanged behavior from the original build.
  function updateRecLegacy(n) {
    var lines = buildLineItems(n);
    if (recLines) {
      if (lines.length > 1) {
        recLines.hidden = false;
        recLines.innerHTML = lines.map(function (l) {
          return '<li><span class="ml-vis__rec-qty">' + l.qty + ' x</span> <span class="ml-vis__rec-name">' + esc(l.label) + '</span></li>';
        }).join('');
      } else {
        recLines.hidden = true;
        recLines.innerHTML = '';
      }
    }
    if (recTotal) { recTotal.hidden = true; recTotal.textContent = ''; }
    if (recBtn) {
      var totalUnits = lines.reduce(function (s, l) { return s + l.qty; }, 0);
      var hasHandle = lines.every(function (l) { return !!l.handle; });
      recBtn.disabled = !hasHandle || totalUnits === 0;
      recBtn.textContent = totalUnits > 1 ? ('Add ' + totalUnits + ' to cart') : 'Add to cart';
      recBtn.onclick = function () { addLineItems(lines); };
    }
    if (recFine) {
      recFine.textContent = (n >= 8)
        ? 'Setups this big often need a custom recommendation. We will follow up after checkout.'
        : '';
    }
  }

  // ---------- Add to cart: model path (one batched call) ----------
  function addModelLines(lines) {
    if (!lines.length) return;
    if (!window.TL || !window.TL.Cart || !window.TL.Cart.addMultiple) return;
    if (recBtn) { recBtn.setAttribute('aria-busy', 'true'); recBtn.disabled = true; }
    var items = lines.map(function (l) { return { id: l.variantId, quantity: l.qty }; });
    // addMultiple announces + opens the cart drawer itself
    window.TL.Cart.addMultiple(items)
      .then(function () {
        if (recBtn) { recBtn.removeAttribute('aria-busy'); recBtn.disabled = false; }
      })
      .catch(function (e) {
        console.error('MultiLink add failed', e);
        if (recBtn) { recBtn.removeAttribute('aria-busy'); recBtn.disabled = false; }
        announce('Sorry, something went wrong adding to cart.');
      });
  }

  // ---------- Add to cart: legacy path (handle -> variant, sequential) ----------
  function addLineItems(lines) {
    if (!lines.length) return;
    if (!window.TL || !window.TL.Cart) return;
    if (recBtn) { recBtn.setAttribute('aria-busy', 'true'); recBtn.disabled = true; }

    // Resolve each handle -> first available variant id, then add sequentially
    var queue = lines.slice();
    var run = function () {
      if (!queue.length) {
        if (recBtn) { recBtn.removeAttribute('aria-busy'); recBtn.disabled = false; }
        if (window.TL.Drawer) window.TL.Drawer.open('#tl-cart-drawer');
        announce('Added to cart.');
        return;
      }
      var l = queue.shift();
      fetch('/products/' + l.handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          var v = (p.variants || []).find(function (x) { return x.available; }) || p.variants[0];
          if (!v) throw new Error('No variant for ' + l.handle);
          return window.TL.Cart.add(v.id, l.qty);
        })
        .then(run)
        .catch(function (e) {
          console.error('MultiLink add failed', e);
          if (recBtn) { recBtn.removeAttribute('aria-busy'); recBtn.disabled = false; }
          announce('Sorry, something went wrong adding to cart.');
        });
    };
    run();
  }

  // ---------- Boot ----------
  loadState();
  scenes.forEach(function (s) { renderPins(s.getAttribute('data-ml-scene-id')); });
  updateUI();
  maybeShowCoachmark();

})();
